import { Mail, MessageCircleQuestion, Rocket, Palette, CreditCard, ArrowLeftRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { CtaLink, PageHero, Reveal } from '@/components/marketing/primitives';
import { cn } from '@/lib/utils';
import { FAQ_GROUPS } from '@/lib/marketingSeo';
import '@/styles/marketing-pages.css';

interface FAQGroup {
  id: string;
  title: string;
  icon: typeof Rocket;
  tint: string;
  items: { q: string; a: string }[];
}

// The questions and answers live in src/lib/marketingSeo.ts so the FAQPage
// structured data and llms.txt always match this page word for word.
const GROUP_STYLE: Record<string, Pick<FAQGroup, 'icon' | 'tint'>> = {
  'getting-started': { icon: Rocket, tint: 'bg-[hsl(var(--mk-sun))]' },
  'domains-branding': { icon: Palette, tint: 'bg-[hsl(var(--mk-lavender))]' },
  'bookings-payments': { icon: CreditCard, tint: 'bg-[hsl(var(--mk-mint))]' },
  'switching-data': { icon: ArrowLeftRight, tint: 'bg-[hsl(var(--mk-sun))]' },
};

const groups: FAQGroup[] = FAQ_GROUPS.map((g) => ({ ...g, ...(GROUP_STYLE[g.id] ?? GROUP_STYLE['getting-started']) }));

const MarketingFAQ = () => (
  <MarketingLayout>
    <PageHero
      eyebrow={
        <>
          <MessageCircleQuestion className="h-3.5 w-3.5 text-[hsl(var(--mk-sun))]" /> FAQ
        </>
      }
      title={
        <>
          Straight answers. <span className="mk-mark mk-mark-block text-[hsl(var(--mk-ink))]">No small print.</span>
        </>
      }
      lede="Everything cleaning company owners ask us before they sign up. If yours isn’t here, just ask."
    >
      <nav aria-label="FAQ topics" className="flex flex-wrap justify-center gap-2">
        {groups.map((g) => (
          <a
            key={g.id}
            href={`#${g.id}`}
            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
          >
            <g.icon className="h-4 w-4 text-[hsl(var(--mk-mint))]" /> {g.title}
          </a>
        ))}
      </nav>
    </PageHero>

    <section className="py-24 md:py-32">
      <div className="container mx-auto grid gap-14 px-4 lg:grid-cols-[0.36fr_0.64fr] lg:gap-16">
        {/* Sticky aside */}
        <aside className="order-2 lg:order-1">
          <div className="space-y-5 lg:sticky lg:top-28">
            <Reveal className="mk-panel rounded-[2rem] p-8">
              <h2 className="font-display text-2xl leading-tight">Still got a question?</h2>
              <p className="mt-3 text-[hsl(var(--mk-muted))]">
                Ask us about plans, domains, or moving over from your current site. We’ll give you a straight answer.
              </p>
              <div className="mt-7 flex flex-col gap-3">
                <CtaLink to="/sales-contact" variant="ink">
                  Ask us anything
                </CtaLink>
                <a
                  href="mailto:hello@thepolishedversion.com"
                  className="flex items-center justify-center gap-2 text-sm font-medium text-[hsl(var(--mk-violet))] hover:underline"
                >
                  <Mail className="h-4 w-4" /> hello@thepolishedversion.com
                </a>
              </div>
            </Reveal>
            <Reveal delay={100} className="mk-ink-bg mk-on-dark rounded-[2rem] p-8">
              <h2 className="font-display text-2xl leading-tight text-white">The fastest answer?</h2>
              <p className="mt-3 text-white/65">
                Ten minutes inside the product beats any FAQ, and the first 30 days are free.
              </p>
              <div className="mt-7 flex flex-col gap-3">
                <CtaLink to="/signup">Start your free trial</CtaLink>
                <Link to="/demo" className="text-center text-sm font-medium text-white/75 hover:text-white">
                  Or click around a live site
                </Link>
              </div>
            </Reveal>
          </div>
        </aside>

        {/* Questions */}
        <div className="order-1 space-y-16 lg:order-2">
          {groups.map((g) => (
            <div key={g.id} id={g.id} className="scroll-mt-28">
              <Reveal className="mb-6 flex items-center gap-4">
                <span className={cn('flex h-11 w-11 items-center justify-center rounded-xl text-[hsl(var(--mk-ink))]', g.tint)}>
                  <g.icon className="h-5 w-5" />
                </span>
                <h2 className="font-display text-2xl sm:text-3xl">{g.title}</h2>
              </Reveal>
              <Reveal delay={80}>
                <Accordion type="single" collapsible>
                  {g.items.map((item, i) => (
                    <AccordionItem key={item.q} value={`${g.id}-${i}`} className="mk-faq-item">
                      <AccordionTrigger className="mk-faq-trigger">{item.q}</AccordionTrigger>
                      <AccordionContent className="pb-6 pr-10 text-base leading-relaxed text-[hsl(var(--mk-muted))]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  </MarketingLayout>
);

export default MarketingFAQ;
